import { FieldError } from './FieldError';

export interface FieldValidationOptions {
  value: string;
  maxLength?: number;
  required?: boolean;
  showRequiredError?: boolean;
  tooLongMessage?: string;
  requiredMessage?: string;
}

export interface FieldValidationState {
  isOver: boolean;
  isMissing: boolean;
  hasError: boolean;
  errorMessage: string | null;
}

export function getFieldValidation({
  value,
  maxLength,
  required,
  showRequiredError,
  tooLongMessage,
  requiredMessage,
}: FieldValidationOptions): FieldValidationState {
  const isOver = maxLength !== undefined && value.length > maxLength;
  const isMissing =
    !!required && !!showRequiredError && value.trim().length === 0;
  const errorMessage = isOver
    ? tooLongMessage ?? null
    : isMissing
      ? requiredMessage ?? null
      : null;
  return { isOver, isMissing, hasError: isOver || isMissing, errorMessage };
}

export const ERROR_BORDER_CLASS = 'border-red-500 focus:ring-red-500 focus:border-red-500';

interface ValidationFooterProps extends FieldValidationOptions {
  charLabel?: string;
  testIdPrefix?: string;
  trailing?: React.ReactNode;
}

export function ValidationFooter({
  value,
  maxLength,
  required,
  showRequiredError,
  tooLongMessage,
  requiredMessage,
  charLabel,
  testIdPrefix,
  trailing,
}: ValidationFooterProps) {
  const { isOver, errorMessage } = getFieldValidation({
    value,
    maxLength,
    required,
    showRequiredError,
    tooLongMessage,
    requiredMessage,
  });
  const length = value.length;
  const showCounter = maxLength !== undefined;

  return (
    <>
      {(showCounter || trailing) && (
        <div className="flex justify-between items-center mt-1">
          {showCounter ? (
            <span
              className={`text-xs ${isOver ? 'text-red-500 font-medium' : 'text-caption'}`}
              data-testid={testIdPrefix ? `${testIdPrefix}-counter` : undefined}
            >
              {length} / {maxLength}
              {charLabel ? ` ${charLabel}` : ''}
            </span>
          ) : (
            <span />
          )}
          {trailing ?? null}
        </div>
      )}
      <FieldError
        data-testid={testIdPrefix ? `${testIdPrefix}-error` : undefined}
      >
        {errorMessage}
      </FieldError>
    </>
  );
}
